import * as Yup from 'yup';
import { isString } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useState, useEffect, useCallback } from 'react';

import { Grid, MenuItem, TextField, Autocomplete } from '@mui/material';

import { useTranslate } from 'src/locales';
import { BusinessEntity, BusinessOption } from 'src/services/utils/utils.types';
import {
  useGetCountriesQuery,
  useLazySearchBusinessInfoByIdQuery,
  useLazySearchBusinessInfoByNameQuery,
} from 'src/services/utils/utils.service';

import { RHFSelect } from 'src/components/hook-form/rhf-select';

import { BusinessErrorCodes } from 'src/types/business-errors';

/**
 * Country, company name and business identifier, backed by the ABR business register lookup.
 *
 * Shared by the public agency/employer enquiry form and the super admin create-tenant dialog so
 * both validate an ABN the same way and offer the same autocomplete.
 *
 * Expects `country_code`, `company_name` and `tax_id` on the surrounding react-hook-form.
 */

const ABN_LENGTH = 11;

/** Yup fragment for the three fields. Spread into the surrounding form's shape. */
export const companyIdentitySchema = (t: (key: string) => string) => ({
  company_name: Yup.string()
    .required(t('auth.validation.company_name.required'))
    .max(100, t('auth.validation.company_name.max'))
    .min(3, t('auth.validation.company_name.min')),
  tax_id: Yup.string()
    .required(t('auth.validation.tax_id.required'))
    .min(5, t('auth.validation.tax_id.min'))
    .max(15, t('auth.validation.tax_id.max'))
    .when('country_code', ([country_code], schema) =>
      country_code === 'AU'
        ? Yup.string()
          .required(t('auth.validation.abn.required'))
          .matches(/^[0-9]+$/, t('auth.validation.abn.numeric'))
          .length(ABN_LENGTH, t('auth.validation.abn.length'))
        : schema
    ),
  country_code: Yup.string().required(t('auth.validation.country_code')),
});

type Props = {
  /** Rendered on its own row when the fields sit in a wider grid. */
  countryFieldWidth?: number;
};

export default function CompanyIdentityFields({ countryFieldWidth = 6 }: Props) {
  const { t } = useTranslate();

  const { setValue, trigger, watch, formState: { errors } } = useFormContext();

  const { data: countries } = useGetCountriesQuery();

  const [searchDisabled, setSearchDisabled] = useState(false);
  const [debouncedSearchTimeout, setDebouncedSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [idResults, setIdResults] = useState<BusinessOption[]>([]);
  const [nameResults, setNameResults] = useState<BusinessEntity[]>([]);
  const [isSearchingById, setIsSearchingById] = useState(false);
  const [isSearchingByName, setIsSearchingByName] = useState(false);

  const [searchBusinessInfoById] = useLazySearchBusinessInfoByIdQuery();
  const [searchBusinessInfoByName] = useLazySearchBusinessInfoByNameQuery();

  const countryCode = watch('country_code');
  const taxId = watch('tax_id');
  const companyName = watch('company_name');

  useEffect(() => {
    setSearchDisabled(false);
    setIdResults([]);
  }, [countryCode]);

  const handleSearchById = useCallback(async (query: string, country: string) => {
    try {
      const response = await searchBusinessInfoById({ query, countryCode: country }).unwrap();

      setIdResults([
        { business_name: response.entity_name, tax_id: response.tax_id },
        ...response.business_names.map((name) => ({ business_name: name, tax_id: response.tax_id })),
      ]);
    } catch (error) {
      if (isString(error?.data?.error_code) && error.data.error_code === BusinessErrorCodes.BusinessInfoCountryNotSupported) {
        setSearchDisabled(true);
      }
      setIdResults([]);
    }
  }, [searchBusinessInfoById]);

  const handleSearchByName = useCallback(async (query: string, country: string) => {
    try {
      const response = await searchBusinessInfoByName({ query, countryCode: country }).unwrap();
      setNameResults(response.results);
    } catch (error) {
      if (isString(error?.data?.error_code) && error.data.error_code === BusinessErrorCodes.BusinessInfoCountryNotSupported) {
        setSearchDisabled(true);
      }
      setNameResults([]);
    }
  }, [searchBusinessInfoByName]);

  const debouncedNameSearch = useCallback((query: string, country: string) => {
    if (debouncedSearchTimeout) clearTimeout(debouncedSearchTimeout);

    setIsSearchingByName(true);
    const timeout = setTimeout(async () => {
      await handleSearchByName(query, country);
      setIsSearchingByName(false);
    }, 500);

    setDebouncedSearchTimeout(timeout);
  }, [debouncedSearchTimeout, handleSearchByName]);

  const debouncedIdSearch = useCallback((query: string, country: string) => {
    if (debouncedSearchTimeout) clearTimeout(debouncedSearchTimeout);

    // an ABN is always 11 digits - don't call the register until we have a complete one
    // (country codes come back uppercase, so compare accordingly)
    if (country?.toUpperCase() === 'AU' && query.length < ABN_LENGTH) {
      setIdResults([]);
      return;
    }

    setIsSearchingById(true);
    const timeout = setTimeout(async () => {
      await handleSearchById(query, country);
      setIsSearchingById(false);
    }, 500);

    setDebouncedSearchTimeout(timeout);
  }, [debouncedSearchTimeout, handleSearchById]);

  useEffect(() => {
    if (countryCode && taxId && !searchDisabled) {
      debouncedIdSearch(taxId, countryCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxId, countryCode, searchDisabled]);

  useEffect(() => {
    if (countryCode && companyName && !searchDisabled) {
      debouncedNameSearch(companyName, countryCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, countryCode, searchDisabled]);

  return (
    <>
      <Grid item xs={12} sm={countryFieldWidth}>
        <RHFSelect
          onChange={(event) => {
            setValue('country_code', event.target.value);
            trigger('tax_id');
          }}
          inputProps={{ 'data-testid': 'country_code' }}
          name="country_code"
          label={t('auth.form-labels.country')}
        >
          {!countries && <MenuItem key="default" value={undefined}>{' '}</MenuItem>}
          {countries?.map((country, index) => (
            <MenuItem key={`country-${country.code}-${index}`} value={country.code}>
              {country.name}
            </MenuItem>
          ))}
        </RHFSelect>
      </Grid>

      <Grid item xs={12}>
        <Autocomplete
          sx={{ flexGrow: 1 }}
          options={nameResults}
          getOptionLabel={(option) => (typeof option === 'string' ? option : `${option.name}`)}
          disabled={!countryCode}
          loading={isSearchingByName && !searchDisabled}
          filterOptions={(options) => options.filter((option) => option.name)}
          freeSolo
          value={companyName}
          onChange={(_, value) => {
            if (typeof value === 'string') {
              setValue('company_name', value, { shouldValidate: true });
            } else if (value) {
              setValue('tax_id', value.tax_id, { shouldValidate: true });
              setValue('company_name', value.name, { shouldValidate: true });
            }
          }}
          onInputChange={(_, value) => setValue('company_name', value, { shouldValidate: true })}
          renderOption={(props, option) => (
            <li {...props}>
              {typeof option === 'string' ? option : `${option.name} (${option.tax_id})`}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('auth.form-labels.company_name')}
              inputProps={{ 'data-testid': 'company_name', ...params.inputProps }}
              error={errors.company_name !== undefined}
              helperText={errors.company_name?.message as string}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Autocomplete
          options={idResults}
          getOptionLabel={(option) => (typeof option === 'string' ? option : `${option.tax_id}`)}
          loading={isSearchingById && !searchDisabled}
          freeSolo
          filterOptions={(options) => options.filter((option) => option.business_name)}
          disabled={!countryCode}
          value={taxId}
          onChange={(_, value) => {
            if (typeof value === 'string') {
              setValue('tax_id', value, { shouldValidate: true });
            } else if (value) {
              setValue('tax_id', value.tax_id, { shouldValidate: true });
              setValue('company_name', value.business_name, { shouldValidate: true });
            }
          }}
          onInputChange={(_, value) => setValue('tax_id', value, { shouldValidate: true })}
          renderOption={(props, option) => (
            <li {...props}>
              {typeof option === 'string' ? option : `${option.business_name} (${option.tax_id})`}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{ 'data-testid': 'tax_id', ...params.inputProps }}
              label={t('auth.form-labels.tax_id')}
              error={errors.tax_id !== undefined}
              helperText={errors.tax_id?.message as string}
            />
          )}
        />
      </Grid>
    </>
  );
}
